// ActivityTimeline — MIDNIGHT elevation
//
// Vertical timeline of activity entries inside DetailDrawer.
// Uses ds-tile-inset for each event row (flat rgba, NO backdrop-filter).
// Connector line: 1px dashed in var(--midnight-border).
// Event type icons rendered in 28×28 round chips from Chip primitive, tinted
// by the event's phase (neutral when it isn't one).
// Caps at 6 entries — expand button reveals rest inline.

import { useState } from 'react'
import {
  Activity, Sparkles, FileText, Briefcase, Receipt,
  CreditCard, Star, MessageSquare, Phone, Clock,
  Check as CheckIcon,
} from 'lucide-react'
import { Chip } from '@/components/ui/Chip'
import type { PastelTint } from '@/components/ui/Chip'
import { PHASE_TONE } from '@/lib/phaseTone'
import type { Record_, ActivityEntry } from './types'
import { SectionHeader } from './_drawerHelpers'
import { formatClockTime, formatDocDate } from '@/lib/format'

/**
 * When an activity happened, in the WORKSPACE zone (UX-041): "Oct 21, 2026,
 * 9:13 AM". Prefers the raw instant (`at`); falls back to the server's string
 * for older payloads.
 */
export function activityWhen(entry: Pick<ActivityEntry, 'when' | 'at'>): string {
  if (entry.at) {
    const day = formatDocDate(entry.at)
    const clock = formatClockTime(entry.at)
    if (day !== '—' && clock !== '—') return `${day}, ${clock}`
  }
  return entry.when
}

// ─── event icon + tint mapping ────────────────────────────────────────────────
// The icon comes from entry.tone (set on creation) or, for legacy entries
// without a tone, from keywords in entry.what.
//
// The tint always comes from what the event says, by phase (lib/phaseTone):
// the server's entry.tone is an event-kind label (a call is "sky", a note
// "mint") and would otherwise paint a phone call as production work. Events
// that aren't a phase of work — calls, messages, notes, edits — stay neutral.

interface EventStyle {
  icon: typeof Activity
  tint: PastelTint
}

function phaseTintFor(w: string): PastelTint {
  if (w.includes('delet'))                                    return PHASE_TONE.late     // destructive
  if (w.includes('cancel') || w.includes('lost') || w.includes('archiv')) return 'neutral'
  if (w.includes('overdue') || w.includes('past due'))        return PHASE_TONE.late
  if (w.includes('paid') || w.includes('payment'))            return PHASE_TONE.paid
  if (w.includes('accepted'))                                 return PHASE_TONE.production
  if (w.includes('invoice'))                                  return PHASE_TONE.billing
  if (w.includes('quote'))                                    return PHASE_TONE.quote
  if (w.includes('complete') || w.includes('done'))           return PHASE_TONE.billing
  if (w.includes('scheduled') || w.includes('job') || w.includes('started')) return PHASE_TONE.production
  if (w.includes('lead') || w.includes('qualified'))          return PHASE_TONE.sales
  if (w.includes('review') || w.includes('star'))             return 'butter'           // star ratings
  return 'neutral'
}

function resolveEventStyle(entry: ActivityEntry): EventStyle {
  const w = entry.what?.toLowerCase() ?? ''
  const tint = phaseTintFor(w)

  // Prefer explicit tone from entry for the icon
  if (entry.tone) {
    // Pick icon from tone
    if (entry.tone === 'mint')     return { icon: CheckIcon, tint }
    if (entry.tone === 'peach')    return { icon: Clock,   tint }
    if (entry.tone === 'butter')   return { icon: FileText, tint }
    if (entry.tone === 'blush')    return { icon: Receipt, tint }
    if (entry.tone === 'lavender') return { icon: Sparkles, tint }
    if (entry.tone === 'sky')      return { icon: MessageSquare, tint }
    return { icon: Activity, tint }
  }

  // Fallback: keyword match for the icon
  if (w.includes('paid') || w.includes('payment'))   return { icon: CreditCard,    tint }
  if (w.includes('invoice'))                         return { icon: Receipt,       tint }
  if (w.includes('quote') || w.includes('quoted'))   return { icon: FileText,      tint }
  if (w.includes('scheduled') || w.includes('job'))  return { icon: Briefcase,     tint }
  if (w.includes('review') || w.includes('star'))    return { icon: Star,          tint }
  if (w.includes('call') || w.includes('phone'))     return { icon: Phone,         tint }
  if (w.includes('message') || w.includes('email'))  return { icon: MessageSquare, tint }
  if (w.includes('lead') || w.includes('qualified')) return { icon: Sparkles,      tint }
  if (w.includes('complete') || w.includes('done'))  return { icon: CheckIcon,     tint }
  return { icon: Activity, tint }
}

// ─── EventRow ─────────────────────────────────────────────────────────────────

function EventRow({ entry, isLast }: { entry: ActivityEntry; isLast: boolean }) {
  const { icon: Icon, tint } = resolveEventStyle(entry)

  return (
    <li
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        position: 'relative',
      }}
    >
      {/* Connector line — drawn from this icon center to next icon center */}
      {!isLast && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 13,              // center of 28px chip
            top: 28,              // below icon bottom
            width: 1,
            bottom: -16,          // into the gap between rows
            borderLeft: '1px dashed rgb(var(--fg-rgb) / 0.08)',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Icon chip — 28×28 round pastel chip */}
      <Chip
        tint={tint}
        size="sm"
        style={{
          width: 28,
          height: 28,
          padding: 0,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon style={{ width: 12, height: 12 }} />
      </Chip>

      {/* Content */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          // ds-tile-inset for event body — flat, no blur
          background: 'rgb(var(--bg-rgb) / 0.25)',
          border: '0.5px solid rgb(var(--fg-rgb) / 0.06)',
          borderRadius: 12,
          padding: '10px 14px',
          marginBottom: 8,
        }}
      >
        <p
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: 'rgb(var(--fg-rgb))',
            lineHeight: 1.35,
            letterSpacing: '-0.01em',
          }}
        >
          {entry.what}
        </p>
        <p
          style={{
            fontSize: 12,
            color: 'var(--midnight-text3)',
            fontVariantNumeric: 'tabular-nums',
            marginTop: 4,
            letterSpacing: '-0.005em',
          }}
        >
          {entry.at ? <time dateTime={entry.at}>{activityWhen(entry)}</time> : entry.when}
        </p>
      </div>
    </li>
  )
}

// ─── ActivityTimeline ─────────────────────────────────────────────────────────

const INITIAL_VISIBLE = 6

interface ActivityTimelineProps {
  record: Record_
  /** The activity feed didn't load: say so instead of "Nothing happened yet". */
  failed?: boolean
}

export function ActivityTimeline({ record, failed = false }: ActivityTimelineProps) {
  const [showAll, setShowAll] = useState(false)

  // Reverse-chronological order preserved
  const items = (record.activity ?? []).slice().reverse()
  const hasMore = items.length > INITIAL_VISIBLE
  const visible = showAll ? items : items.slice(0, INITIAL_VISIBLE)

  return (
    <div>
      <SectionHeader tone="neutral" icon={Activity} title="Activity" />

      {items.length === 0 ? (
        <p
          role={failed ? 'status' : undefined}
          style={{
            fontSize: 13,
            // Was rgb(var(--fg-rgb) / 0.22) ≈ 1.9:1 (UX-012): the shared tertiary token.
            color: 'var(--midnight-text3)',
            padding: '8px 0 4px',
            letterSpacing: '-0.005em',
          }}
        >
          {failed
            ? "Couldn't load the activity. Close and reopen to try again."
            : 'Nothing happened yet.'}
        </p>
      ) : (
        <>
          <ol
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {visible.map((entry, i) => (
              <EventRow
                key={i}
                entry={entry}
                isLast={i === visible.length - 1}
              />
            ))}
          </ol>

          {hasMore && (
            <div style={{ marginTop: 4 }}>
              <Chip
                tint="neutral"
                onClick={() => setShowAll((v) => !v)}
                style={{ fontSize: 13 }}
              >
                {showAll
                  ? 'Show less'
                  : `Show all (${items.length})`}
              </Chip>
            </div>
          )}
        </>
      )}
    </div>
  )
}
